import { Button, TextField } from '@mui/material'
import { SearchIcon } from 'assets/icons'
import CountrySelect from 'components/form/CountrySelect'
import CountryStateSelect from 'components/form/CountryStateSelect'
import { FI_COUNTRY_ID, SE_COUNTRY_ID } from 'config'
import { UseFormReturn } from 'react-hook-form'
import { useIntl } from 'react-intl'
import { useSelector } from 'react-redux'
import { selectAuth } from 'store/authentication/slice'
import { selectCountriesWithClubs } from 'store/countries/slice'
import { selectStates } from 'store/countryStates/slice'
import styled from 'styled-components'

interface Props {
  countriesWithStates: Country[]
  form: UseFormReturn<CourseEditorSearchForm>
  handleCountryChange: (countries: Country[]) => void
  onSubmit: (data: any) => void
}

export default function SearchBar({ countriesWithStates, form, handleCountryChange, onSubmit }: Props): JSX.Element {
  const intl = useIntl()
  const { register, control, watch } = form
  const countryStates = useSelector(selectStates)
  const { user, status: userStatus } = useSelector(selectAuth)
  const { countries, status: countryStatus } = useSelector(selectCountriesWithClubs)

  const isLoading = countryStatus === 'loading' || userStatus === 'loading'

  const getInitialCountryValue = (userCountryId: number | undefined): SelectOption => {
    const disabledCountryIds = [FI_COUNTRY_ID, SE_COUNTRY_ID]

    if (userCountryId && !disabledCountryIds.includes(userCountryId)) {
      const userCountry = countries.find((country: Country) => country.id === userCountryId)
      if (userCountry) {
        return { key: userCountry.id, label: userCountry.name }
      }
    }

    return { key: 'all', label: intl.formatMessage({ id: 'strings.allCountries' }) }
  }

  return (
    <SearchForm onSubmit={onSubmit}>
      {isLoading ? null : (
        <>
          <SearchInput
            variant="outlined"
            placeholder={intl.formatMessage({ id: 'courseEditor.searchPlaceholder' })}
            {...(register ? register('courseOrClubName') : {})}
          />
          <SelectContainer>
            <CountrySelect
              name="country"
              control={control}
              initialValue={getInitialCountryValue(user?.countryId || undefined)}
              onlyCountriesWithClubs
              label="Location"
              handleCountryChange={handleCountryChange}
              disabledCountries={[FI_COUNTRY_ID, SE_COUNTRY_ID]}
              courseEditor
            />
            {countriesWithStates.length > 0 && (
              <CountryStateSelect
                name="state"
                control={control}
                countryStates={countryStates}
                disabled={countriesWithStates.length === 0 && !watch('state')}
                value={watch('state')}
                courseEditor
              />
            )}
          </SelectContainer>
          <SearchButton type="submit" startIcon={<SearchIcon width="24" height="24" fill="#fff" />}>
            {intl.formatMessage({ id: 'strings.search' })}
          </SearchButton>
        </>
      )}
    </SearchForm>
  )
}

const SearchForm = styled.form`
  display: flex;
  gap: 10px;
  margin-top: 60px;
  flex-wrap: wrap;
`

const SearchInput = styled(TextField)`
  width: 505px;
  height: 53px;
  margin-top: 6px;

  @media (max-width: 1080px) {
    max-width: 400px;
  }

  .MuiOutlinedInput-input {
    padding: 0 14px;
    height: 53px;
  }
`

const SearchButton = styled(Button)`
  align-items: center;
  background: ${(props) => props.theme.palette.primary.main};
  color: ${(props) => props.theme.customPalette.light};
  display: flex;
  font-size: 16px;
  gap: 8px;
  height: 53px;
  justify-content: center;
  margin-top: 6px;
  padding: 8px 24px;
  width: 133px;
`

const SelectContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 260px;
  row-gap: 10px;
`
